import { useEffect } from 'react'

interface SEOProps {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: string
  keywords?: string
}

// Helper to ensure we have a valid string value
const ensureString = (value: string | undefined, fallback: string): string => {
  if (!value || value === 'undefined' || value.trim() === '') {
    return fallback
  }
  return value
}

export function SEO({ title, description, image, url, type = 'website', keywords }: SEOProps) {
  useEffect(() => {
    // Ensure we have valid title and description with fallbacks
    const validTitle = ensureString(title, 'Michela')
    const validDescription = ensureString(description, 'Your personal styling assistant')
    const validKeywords = ensureString(keywords, 'personal stylist, AI stylist, wardrobe organizer, outfit generator, fashion AI')

    // Update document title
    document.title = validTitle

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      if (!content || content === 'undefined' || content.trim() === '') {
        return // Skip empty values
      }

      const attribute = property ? 'property' : 'name'
      let element = document.querySelector(`meta[${attribute}="${name}"]`)

      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attribute, name)
        document.head.appendChild(element)
      }

      element.setAttribute('content', content)
    }

    updateMetaTag('description', validDescription)
    updateMetaTag('keywords', validKeywords)
    updateMetaTag('og:title', validTitle, true)
    updateMetaTag('og:description', validDescription, true)
    updateMetaTag('og:type', type, true)
    updateMetaTag('twitter:title', validTitle, true)
    updateMetaTag('twitter:description', validDescription, true)

    // Always use OG image - default to /og-image.png if not provided
    const validImage = ensureString(image, '/og-image.png')
    // Convert relative URLs to absolute URLs for OG tags
    const imageUrl = validImage.startsWith('http') 
      ? validImage 
      : `${window.location.origin}${validImage.startsWith('/') ? validImage : '/' + validImage}`
    
    updateMetaTag('og:image', imageUrl, true)
    updateMetaTag('og:image:width', '1200', true)
    updateMetaTag('og:image:height', '630', true)
    updateMetaTag('twitter:image', imageUrl, true)

    const validUrl = ensureString(url, '')
    if (validUrl) {
      updateMetaTag('og:url', validUrl, true)
      updateMetaTag('twitter:url', validUrl, true)

      // Update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute('href', validUrl)
    }
  }, [title, description, image, url, type])

  return null
}
